/**
 * AI Service
 *
 * This service handles all AI-powered document processing operations.
 * It integrates with Firebase Cloud Functions which securely process
 * documents using OpenAI GPT-4 Vision API or Gemini 3.0 Flash.
 *
 * Features:
 * - Document categorization with confidence scoring
 * - Smart title generation
 * - Content summarization
 * - Data extraction from documents
 * - Fallback handling for AI failures
 *
 * Security:
 * - Cloud Functions handle API keys securely on server
 * - User authentication required for all AI operations
 * - No sensitive API keys exposed to browser
 * - Request validation and sanitization
 * - Error handling without exposing sensitive data
 *
 * Architecture:
 * - Browser uploads document → Cloud Function processes with AI
 * - API keys never leave secure server environment
 * - Rate limiting handled server-side by Cloud Functions
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
// Import Firebase app instance from shared package
import { app } from '@trip-planner-monorepo/shared';
import { AI_PROMPTS } from '../config/aiPrompts';
import { configService } from './configService';
import { AI_ENVIRONMENT, logEnvironmentStatus } from '../config/environment';
import { aiErrorHandler, handleAIError, createFallbackResponse } from './errorHandler';

// AI Service Configuration (using environment configuration)
const AI_CONFIG = {
  // OpenAI models (used by Cloud Functions)
  OPENAI_VISION_MODEL: 'gpt-4o',
  OPENAI_TEXT_MODEL: 'gpt-4o',
  // Vertex document processing (Gemini 2.5 Flash replacement)
  GEMINI_MODEL: 'gemini-3.5-flash-lite',

  // File size limits for AI processing
  MAX_IMAGE_SIZE: 20 * 1024 * 1024, // 20MB
  MAX_PDF_SIZE: 20 * 1024 * 1024, // 20MB
  MAX_PDF_PAGES: 10,

  // Default confidence thresholds
  MIN_CATEGORIZATION_CONFIDENCE: 0.7,
  MIN_EXTRACTION_CONFIDENCE: 0.6
};

// Log environment status on service initialization
if (AI_ENVIRONMENT.AI_DEBUG_MODE) {
  logEnvironmentStatus();
}

/**
 * NOTE: API key management and rate limiting are now handled securely
 * by Firebase Cloud Functions. The following functions have been removed:
 * - getApiKey(): API keys never exposed to browser
 * - RateLimiter class: Rate limiting handled server-side
 * - makeOpenAIRequest(): Direct OpenAI calls replaced with Cloud Functions
 */

/**
 * Converts file to base64 for AI Vision API
 * @param {File|Blob} file - The file to convert
 * @returns {Promise<string>} Base64 encoded file data
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Calls Firebase Cloud Function for AI document processing
 * @param {Object} requestData - The request data for Cloud Function
 * @returns {Promise<string>} The AI response content
 * @throws {Error} If Cloud Function call fails
 */
const callDocumentProcessingFunction = async (requestData) => {
  try {
    // Debug logging
    console.log('🔍 Debug - app instance:', app);
    console.log('🔍 Debug - getFunctions available:', typeof getFunctions);

    // Check if Firebase app is initialized
    if (!app) {
      console.error('❌ Firebase app not initialized');
      throw new Error('FUNCTIONS_UNAVAILABLE');
    }

    let functions;
    try {
      functions = getFunctions(app);
      console.log('🔍 Debug - functions instance:', functions);
      console.log('🔍 Debug - functions type:', typeof functions);
      console.log('🔍 Debug - functions is null?:', functions === null);
      console.log('🔍 Debug - functions is undefined?:', functions === undefined);
    } catch (error) {
      console.error('❌ Error calling getFunctions:', error);
      throw new Error('FUNCTIONS_UNAVAILABLE');
    }

    // Check if functions service is available
    if (!functions) {
      console.warn('⚠️ Cloud Functions service not available. Running in local development mode.');
      console.warn('ℹ️ To enable AI features: Deploy Cloud Functions or run Firebase emulator');
      throw new Error('FUNCTIONS_UNAVAILABLE');
    }

    // Select Cloud Function based on AI provider
    const provider = AI_ENVIRONMENT.AI_PROVIDER;
    const functionName = provider === 'gemini'
      ? 'processDocumentWithGemini'
      : 'processDocumentWithAI';

    if (AI_ENVIRONMENT.AI_DEBUG_MODE) {
      console.log(`🤖 Using AI provider: ${provider} (function: ${functionName})`);
    }

    const processingFunction = httpsCallable(functions, functionName);

    const result = await processingFunction(requestData);

    if (!result.data.success) {
      throw new Error(result.data.error || 'AI processing failed');
    }

    if (AI_ENVIRONMENT.AI_DEBUG_MODE) {
      console.log('Cloud Function processing successful:', {
        provider,
        model: result.data.model,
        processingType: result.data.processingType,
        tokensUsed: result.data.usage?.totalTokens
      });
    }

    return result.data.content;
  } catch (error) {
    // Handle Firebase Functions not available (local development without emulator)
    if (error.message && (
      error.message.includes('functions is not available') ||
      error.message.includes('FUNCTIONS_UNAVAILABLE')
    )) {
      console.warn('⚠️ Cloud Functions not available. Running in local development mode.');
      console.warn('ℹ️ To enable AI features: Deploy Cloud Functions or run Firebase emulator');
      throw new Error('FUNCTIONS_UNAVAILABLE');
    }

    // Handle Firebase Functions errors
    if (error.code === 'unauthenticated') {
      throw new Error('You must be logged in to use AI document processing features.');
    }

    if (error.code === 'resource-exhausted') {
      throw new Error('AI service rate limit exceeded. Please try again in a moment.');
    }

    if (error.code === 'invalid-argument') {
      throw new Error(`Invalid request: ${error.message}`);
    }

    // Generic error
    throw new Error(error.message || 'AI document processing failed. Please try again later.');
  }
};

/**
 * Processes a multimodal document (image or PDF) using AI via Cloud Function
 * @param {File} file - The file to process
 * @param {string} prompt - The prompt for analysis
 * @returns {Promise<string>} The AI response
 */
const processMultimodalDocument = async (file, prompt, options = {}) => {
  const isPdf = file.type === 'application/pdf';
  const maxSize = isPdf ? AI_CONFIG.MAX_PDF_SIZE : AI_CONFIG.MAX_IMAGE_SIZE;

  if (file.size > maxSize) {
    throw new Error(`File too large. Maximum size is ${maxSize / 1024 / 1024}MB.`);
  }

  const base64Data = await fileToBase64(file);

  const requestData = {
    prompt,
    base64Image: base64Data, // Using base64Image field for compatibility
    mimeType: file.type,
    model: AI_CONFIG.OPENAI_VISION_MODEL,
    temperature: 0.1, // Low temperature for consistent results
    maxTokens: 1000,
    ...options
  };

  return await callDocumentProcessingFunction(requestData);
};

/**
 * Processes a text-based document using OpenAI API via Cloud Function
 * @param {string} text - The extracted text content
 * @param {string} prompt - The prompt for analysis
 * @returns {Promise<string>} The AI response
 */
const processTextDocument = async (text, prompt, options = {}) => {
  const requestData = {
    prompt: `${prompt}\n\nDocument content:\n${text}`,
    extractedText: text,
    model: AI_CONFIG.OPENAI_TEXT_MODEL,
    temperature: 0.1,
    maxTokens: 1000,
    ...options
  };

  return await callDocumentProcessingFunction(requestData);
};

/**
 * Extracts text from PDF files using PDF.js from CDN
 * @param {File} file - The PDF file
 * @returns {Promise<string>} Extracted text content
 */
const extractTextFromPDF = async (file) => {
  try {
    // Load PDF.js from CDN if not already loaded
    if (typeof window.pdfjsLib === 'undefined') {
      await loadPDFJS();
    }

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let fullText = '';
    const maxPages = Math.min(pdf.numPages, AI_CONFIG.MAX_PDF_PAGES);

    for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map(item => item.str)
        .join(' ')
        .trim();

      if (pageText) {
        fullText += `Page ${pageNum}: ${pageText}\n\n`;
      }
    }

    if (fullText.trim()) {
      return fullText.trim();
    } else {
      console.warn('No text extracted from PDF, using filename as fallback');
      return `PDF Document: ${file.name}`;
    }
  } catch (error) {
    console.error('Error extracting text from PDF:', error);
    return `PDF Document: ${file.name} (Text extraction failed: ${error.message})`;
  }
};

/**
 * Extracts text from various text-based file formats
 * @param {File} file - The text file
 * @returns {Promise<string>} Extracted text content
 */
const extractTextFromFile = async (file) => {
  try {
    const text = await file.text();

    // Handle different text formats
    switch (file.type) {
      case 'text/plain':
      case 'text/csv':
      case 'application/json':
      case 'application/xml':
      case 'text/xml':
      case 'text/html':
        return text;

      case 'text/rtf':
      case 'application/rtf':
        // Basic RTF processing - strip RTF codes for plain text
        return text.replace(/\\[a-z]+\d*\s?/g, '').replace(/[{}]/g, '').trim();

      default:
        console.warn(`Text extraction for ${file.type} not fully implemented, returning raw content`);
        return text;
    }
  } catch (error) {
    console.error('Error extracting text from file:', error);
    return `Text Document: ${file.name} (Text extraction failed: ${error.message})`;
  }
};

/**
 * Extracts text from Office and OpenDocument formats
 * Note: This is a placeholder. For production, consider using libraries like:
 * - mammoth.js for .docx files
 * - xlsx for Excel files
 * - A server-side solution for complex document parsing
 * @param {File} file - The document file
 * @returns {Promise<string>} Extracted text content
 */
const extractTextFromOfficeDocument = async (file) => {
  console.warn(`Office document text extraction for ${file.type} not implemented. Using filename as fallback.`);

  // Determine document type for better fallback description
  let docType = 'Document';
  if (file.type.includes('word') || file.type.includes('opendocument.text')) {
    docType = 'Word Document';
  } else if (file.type.includes('excel') || file.type.includes('spreadsheet')) {
    docType = 'Spreadsheet';
  } else if (file.type.includes('powerpoint') || file.type.includes('presentation')) {
    docType = 'Presentation';
  }

  return `${docType}: ${file.name}`;
};

/**
 * Extracts text from email files (.eml, .msg)
 * @param {File} file - The email file
 * @returns {Promise<string>} Extracted text content
 */
const extractTextFromEmail = async (file) => {
  try {
    const text = await file.text();

    // Basic email parsing (extract from/to/subject/body)
    const emailData = {
      from: '',
      to: '',
      subject: '',
      body: ''
    };

    // Parse email headers and body
    const lines = text.split('\n');
    let inBody = false;

    for (const line of lines) {
      if (!inBody) {
        if (line.startsWith('From:')) emailData.from = line.substring(5).trim();
        if (line.startsWith('To:')) emailData.to = line.substring(3).trim();
        if (line.startsWith('Subject:')) emailData.subject = line.substring(8).trim();
        if (line.trim() === '') inBody = true; // Empty line marks start of body
      } else {
        emailData.body += line + '\n';
      }
    }

    return `Email Document
From: ${emailData.from}
To: ${emailData.to}
Subject: ${emailData.subject}

${emailData.body.trim()}`;
  } catch (error) {
    console.error('Error extracting text from email:', error);
    return `Email Document: ${file.name} (Extraction failed: ${error.message})`;
  }
};

/**
 * Determines the appropriate text extraction method based on file type
 * @param {File} file - The file to extract text from
 * @returns {Promise<string>} Extracted text content
 */
const extractTextBasedOnType = async (file) => {
  // PDF files
  if (file.type === 'application/pdf') {
    return await extractTextFromPDF(file);
  }

  // Email files
  if (file.type === 'message/rfc822' ||
    file.type === 'application/vnd.ms-outlook' ||
    file.name.endsWith('.eml') ||
    file.name.endsWith('.msg')) {
    return await extractTextFromEmail(file);
  }

  // Plain text and web formats (including HTML)
  if (['text/plain', 'text/csv', 'application/json', 'application/xml', 'text/xml', 'text/html', 'application/xhtml+xml', 'text/rtf', 'application/rtf'].includes(file.type)) {
    return await extractTextFromFile(file);
  }

  // Office and OpenDocument formats
  if (file.type.includes('officedocument') || file.type.includes('ms-') || file.type.includes('opendocument')) {
    return await extractTextFromOfficeDocument(file);
  }

  // Fallback
  console.warn(`No text extraction method for ${file.type}, using filename as fallback`);
  return `Document: ${file.name}`;
};

/**
 * Loads PDF.js library from CDN
 * @returns {Promise<void>}
 */
const loadPDFJS = () => {
  return new Promise((resolve, reject) => {
    if (typeof window.pdfjsLib !== 'undefined') {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      // Set worker path
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load PDF.js library'));
    };
    document.head.appendChild(script);
  });
};

/**
 * Parses JSON response from AI with fallback handling
 * @param {string} response - The AI response text
 * @param {Object} fallback - Fallback object if parsing fails
 * @returns {Object} Parsed JSON or fallback object
 */
const parseAIResponse = (response, fallback = {}) => {
  // Handle empty or null response
  if (!response || typeof response !== 'string' || !response.trim()) {
    console.warn('AI response is empty or invalid');
    return fallback;
  }

  const cleanResponse = response.trim();

  try {
    // 1. Try direct parse (handles pure JSON)
    try {
      return JSON.parse(cleanResponse);
    } catch (e) {
      // Continue to next method
    }

    // 2. Strip markdown code blocks (common in Gemini output)
    // Remove ```json or ``` at start and ``` at end
    const markdownStripped = cleanResponse
      .replace(/^```json\s*/i, '')
      .replace(/^```\s*/i, '')
      .replace(/\s*```$/, '');

    try {
      return JSON.parse(markdownStripped);
    } catch (e) {
      // Continue to regex extraction
    }

    // 3. Try to extract JSON object using regex (handles embedded JSON)
    // Matches from first { to last }
    const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0]);
    }

    // If no JSON found, return fallback and log raw response for debugging
    console.warn('No JSON found in AI response. Raw response:', cleanResponse);
    return fallback;
  } catch (error) {
    console.warn('Failed to parse AI response as JSON:', error.message);
    console.warn('Raw response that failed parsing:', cleanResponse);
    return fallback;
  }
};

// --- JSON Schemas for Structured Output ---

const categorizationSchema = {
  type: "OBJECT",
  properties: {
    categories: {
      type: "ARRAY",
      items: {
        type: "OBJECT",
        properties: {
          id: { type: "STRING" },
          confidence: { type: "NUMBER" }
        },
        required: ["id", "confidence"]
      }
    },
    primaryCategory: { type: "STRING" },
    reasoning: { type: "STRING" }
  },
  required: ["categories", "primaryCategory", "reasoning"]
};

const titleSchema = {
  type: "OBJECT",
  properties: {
    title: { type: "STRING" }
  },
  required: ["title"]
};

const summarySchema = {
  type: "OBJECT",
  properties: {
    summary: { type: "STRING" }
  },
  required: ["summary"]
};

const dataExtractionSchema = {
  type: "OBJECT",
  properties: {
    dates: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 },
    locations: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 },
    names: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 },
    referenceNumbers: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 },
    amounts: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 },
    terms: { type: "ARRAY", items: { type: "STRING" }, maxItems: 5 }
  },
  required: ["dates", "locations", "names", "referenceNumbers", "amounts", "terms"]
};

// --- Entity-Specific Extraction Schemas ---

const flightExtractionSchema = {
  type: "OBJECT",
  properties: {
    airline: { type: "STRING" },
    flightNumber: { type: "STRING" },
    departureCity: { type: "STRING" },
    departureAirport: { type: "STRING" },
    arrivalCity: { type: "STRING" },
    arrivalAirport: { type: "STRING" },
    departureTime: { type: "STRING" },
    arrivalTime: { type: "STRING" },
    bookingReference: { type: "STRING" },
    ticketNumbers: { type: "ARRAY", items: { type: "STRING" } },
    passengers: { type: "ARRAY", items: { type: "STRING" } },
    classOfService: { type: "STRING" },
    baggageAllowance: { type: "STRING" },
    seatNumbers: { type: "ARRAY", items: { type: "STRING" } },
    cost: { type: "STRING" }
  },
  required: []
};

const hotelExtractionSchema = {
  type: "OBJECT",
  properties: {
    hotelName: { type: "STRING" },
    address: { type: "STRING" },
    city: { type: "STRING" },
    country: { type: "STRING" },
    checkInDate: { type: "STRING" },
    checkOutDate: { type: "STRING" },
    checkInTime: { type: "STRING" },
    checkOutTime: { type: "STRING" },
    roomType: { type: "STRING" },
    numberOfRooms: { type: "NUMBER" },
    numberOfGuests: { type: "NUMBER" },
    guestNames: { type: "ARRAY", items: { type: "STRING" } },
    bookingReference: { type: "STRING" },
    cost: { type: "STRING" },
    costPerNight: { type: "STRING" },
    amenities: { type: "ARRAY", items: { type: "STRING" } },
    cancellationPolicy: { type: "STRING" },
    contactPhone: { type: "STRING" },
    contactEmail: { type: "STRING" }
  },
  required: []
};

const carExtractionSchema = {
  type: "OBJECT",
  properties: {
    rentalCompany: { type: "STRING" },
    bookingReference: { type: "STRING" },
    pickupLocation: { type: "STRING" },
    returnLocation: { type: "STRING" },
    pickupTime: { type: "STRING" },
    returnTime: { type: "STRING" },
    vehicleClass: { type: "STRING" },
    vehicleModel: { type: "STRING" },
    driverName: { type: "STRING" },
    additionalDrivers: { type: "ARRAY", items: { type: "STRING" } },
    cost: { type: "STRING" },
    costPerDay: { type: "STRING" },
    insuranceIncluded: { type: "BOOLEAN" },
    mileagePolicy: { type: "STRING" },
    fuelPolicy: { type: "STRING" }
  },
  required: []
};

// Map entity types to their extraction schemas
const entityExtractionSchemas = {
  flight: flightExtractionSchema,
  hotel: hotelExtractionSchema,
  car: carExtractionSchema
};

/**
 * AI Service Object
 * Main interface for all AI-powered document processing
 */
export const aiService = {
  /**
   * Categorizes a document using AI analysis
   * @param {File} file - The document file
   * @param {string} extractedText - Optional pre-extracted text
   * @returns {Promise<Object>} Categorization results with confidence scores
   */
  async categorizeDocument(file, extractedText = null) {
    try {
      // Check if AI categorization is enabled
      if (!AI_ENVIRONMENT.AI_CATEGORIZATION_ENABLED) {
        return createFallbackResponse(file.name, { reason: 'AI categorization disabled' });
      }

      const prompt = AI_PROMPTS.categorization;
      let response;

      if (file.type.startsWith('image/') || (AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf')) {
        response = await processMultimodalDocument(file, prompt, {
          responseFormat: 'json',
          responseSchema: categorizationSchema,
          maxTokens: 8192
        });
      } else {
        const text = extractedText || await extractTextBasedOnType(file);
        response = await processTextDocument(text, prompt, {
          responseFormat: 'json',
          responseSchema: categorizationSchema,
          maxTokens: 8192
        });
      }

      const result = parseAIResponse(response, {
        categories: [{ id: 'uncategorized', confidence: 0.5 }],
        primaryCategory: 'uncategorized',
        reasoning: 'AI categorization failed, using fallback'
      });

      // Validate confidence scores
      if (result.categories) {
        // Filter by threshold
        const validCategories = result.categories.filter(cat =>
          cat.confidence >= AI_CONFIG.MIN_CATEGORIZATION_CONFIDENCE
        );

        if (validCategories.length > 0) {
          // Find highest confidence category
          const bestCategory = validCategories.reduce((prev, current) =>
            (prev.confidence > current.confidence) ? prev : current
          );

          // Assign only the best category
          result.categories = [bestCategory];
          result.primaryCategory = bestCategory.id;
        } else {
          result.categories = [];
        }
      }

      // Ensure we have at least one category
      if (!result.categories || result.categories.length === 0) {
        result.categories = [{ id: 'uncategorized', confidence: 0.5 }];
        result.primaryCategory = 'uncategorized';
      }

      return {
        ...result,
        processedAt: new Date().toISOString(),
        aiModel: AI_ENVIRONMENT.AI_PROVIDER === 'gemini' ? AI_CONFIG.GEMINI_MODEL : (file.type.startsWith('image/') ? AI_CONFIG.OPENAI_VISION_MODEL : AI_CONFIG.OPENAI_TEXT_MODEL)
      };
    } catch (error) {
      const errorInfo = handleAIError(error, {
        operation: 'categorization',
        fileName: file.name,
        fileType: file.type
      });

      if (AI_ENVIRONMENT.FALLBACK_TO_BASIC_ON_AI_FAILURE) {
        console.warn('AI categorization failed, using fallback:', errorInfo.userMessage);
        return createFallbackResponse(file.name, { error: errorInfo });
      } else {
        throw new Error(errorInfo.userMessage);
      }
    }
  },

  /**
   * Generates a smart title for a document
   * @param {File} file - The document file
   * @param {string} extractedText - Optional pre-extracted text
   * @returns {Promise<string>} Generated title
   */
  async generateTitle(file, extractedText = null) {
    try {
      // Check if AI title generation is enabled
      if (!AI_ENVIRONMENT.AI_TITLE_GENERATION_ENABLED) {
        return file.name;
      }

      const prompt = AI_PROMPTS.titleGeneration;
      let response;
      const options = {
        responseFormat: 'json',
        responseSchema: titleSchema,
        maxTokens: 1000
      };

      if (file.type.startsWith('image/') || (AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf')) {
        response = await processMultimodalDocument(file, prompt, options);
      } else {
        const text = extractedText || await extractTextBasedOnType(file);
        response = await processTextDocument(text, prompt, options);
      }

      const result = parseAIResponse(response, { title: file.name });

      // Clean up the response and ensure it's reasonable length
      let title = (result.title || file.name).trim();
      if (title.length > 60) {
        title = title.substring(0, 57) + '...';
      }

      return title;
    } catch (error) {
      const errorInfo = handleAIError(error, {
        operation: 'titleGeneration',
        fileName: file.name,
        fileType: file.type
      });

      console.warn('AI title generation failed, using filename:', errorInfo.userMessage);
      return file.name; // Always fallback to filename for titles
    }
  },

  /**
   * Generates a summary of the document content
   * @param {File} file - The document file
   * @param {string} extractedText - Optional pre-extracted text
   * @returns {Promise<string>} Generated summary
   */
  async generateSummary(file, extractedText = null) {
    try {
      const prompt = AI_PROMPTS.summaryGeneration;
      let response;
      const options = {
        responseFormat: 'json',
        responseSchema: summarySchema,
        maxTokens: 1000
      };

      if (file.type.startsWith('image/') || (AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf')) {
        response = await processMultimodalDocument(file, prompt, options);
      } else {
        const text = extractedText || await extractTextBasedOnType(file);
        response = await processTextDocument(text, prompt, options);
      }

      const result = parseAIResponse(response, { summary: `Document: ${file.name}` });
      return result.summary || `Document: ${file.name}`;
    } catch (error) {
      console.error('AI summary generation failed:', error);
      return `Document: ${file.name}`; // Fallback
    }
  },

  /**
   * Extracts structured data from a document
   * @param {File} file - The document file
   * @param {string} extractedText - Optional pre-extracted text
   * @returns {Promise<Object>} Extracted structured data
   */
  async extractData(file, extractedText = null) {
    try {
      const prompt = AI_PROMPTS.dataExtraction;
      let response;

      if (file.type.startsWith('image/') || (AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf')) {
        response = await processMultimodalDocument(file, prompt, {
          responseFormat: 'json',
          responseSchema: dataExtractionSchema,
          maxTokens: 8192
        });
      } else {
        const text = extractedText || await extractTextBasedOnType(file);
        response = await processTextDocument(text, prompt, {
          responseFormat: 'json',
          responseSchema: dataExtractionSchema,
          maxTokens: 8192
        });
      }

      const result = parseAIResponse(response, {
        documentType: 'unknown',
        confidence: 0.5,
        data: {}
      });

      return {
        ...result,
        extractedAt: new Date().toISOString(),
        aiModel: AI_ENVIRONMENT.AI_PROVIDER === 'gemini' ? AI_CONFIG.GEMINI_MODEL : (file.type.startsWith('image/') ? AI_CONFIG.OPENAI_VISION_MODEL : AI_CONFIG.OPENAI_TEXT_MODEL)
      };
    } catch (error) {
      console.error('AI data extraction failed:', error);
      return {
        documentType: 'unknown',
        confidence: 0.5,
        data: {},
        error: error.message,
        extractedAt: new Date().toISOString()
      };
    }
  },

  /**
   * Processes a complete document with all AI features
   * @param {File} file - The document file
   * @returns {Promise<Object>} Complete AI processing results
   */
  async processDocument(file) {
    try {
      // Extract text once for non-image/non-PDF(Gemini) files to avoid multiple extractions
      let extractedText = null;
      const isGeminiPdf = AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf';

      if (!file.type.startsWith('image/') && !isGeminiPdf) {
        extractedText = await extractTextBasedOnType(file);
      }

      // Run all AI processing in parallel for efficiency
      const [categorization, title, summary, extractedData] = await Promise.allSettled([
        this.categorizeDocument(file, extractedText),
        this.generateTitle(file, extractedText),
        this.generateSummary(file, extractedText),
        this.extractData(file, extractedText)
      ]);

      // Collect successful results and log failures
      const results = {
        categorization: categorization.status === 'fulfilled' ? categorization.value : null,
        title: title.status === 'fulfilled' ? title.value : file.name,
        summary: summary.status === 'fulfilled' ? summary.value : null,
        extractedData: extractedData.status === 'fulfilled' ? extractedData.value : null,
        processedAt: new Date().toISOString(),
        errors: []
      };

      // Log any failures
      [categorization, title, summary, extractedData].forEach((result, index) => {
        if (result.status === 'rejected') {
          const operation = ['categorization', 'title', 'summary', 'extraction'][index];
          console.error(`AI ${operation} failed:`, result.reason);
          results.errors.push(`${operation}: ${result.reason.message}`);
        }
      });

      return results;
    } catch (error) {
      console.error('Complete AI document processing failed:', error);
      throw new Error(`AI document processing failed: ${error.message}`);
    }
  },

  /**
   * Checks if AI service is available and configured
   * @returns {Promise<boolean>} True if AI service is available
   */
  async isAvailable() {
    try {
      // Read current environment values (not cached AI_ENVIRONMENT)
      // This allows tests to modify process.env and have availability check reflect those changes
      const currentAiEnabled = process.env.REACT_APP_AI_ENABLED !== 'false';
      const currentMockMode = process.env.REACT_APP_AI_MOCK_MODE === 'true';

      // Check if AI is enabled in environment
      if (!currentAiEnabled) {
        return false;
      }

      // Check if mock mode is enabled (useful for testing)
      if (currentMockMode) {
        return true;
      }

      // Note: API key configuration is now handled by Cloud Functions
      // User must be authenticated for AI features to work
      // Cloud Functions will handle API key validation

      return true;
    } catch (error) {
      const errorInfo = handleAIError(error, { operation: 'availability_check' });
      console.warn('AI service not available:', errorInfo.userMessage);
      return false;
    }
  },

  /**
   * Extracts entity-specific data from a file for entity creation
   * This is used for on-demand extraction when user initiates entity creation from a document
   * 
   * @param {File} file - The document file to extract from
   * @param {string} entityType - Target entity type ('flight', 'hotel', etc.)
   * @param {Object} tripContext - Optional trip context with startDate and endDate for date inference
   * @returns {Promise<Object>} Extracted data formatted for entity form
   */
  async extractDataForEntity(file, entityType, tripContext = null) {
    try {
      // Get the appropriate schema and prompt for this entity type
      const schema = entityExtractionSchemas[entityType];
      if (!schema) {
        throw new Error(`No extraction schema defined for entity type: ${entityType}`);
      }

      // Get the prompt from AI_PROMPTS
      const promptKey = `${entityType}Extraction`;
      const promptTemplate = AI_PROMPTS[promptKey];
      if (!promptTemplate) {
        throw new Error(`No extraction prompt defined for entity type: ${entityType}`);
      }

      // Handle function-style prompts that accept trip context
      const prompt = typeof promptTemplate === 'function'
        ? promptTemplate(tripContext)
        : promptTemplate;

      let response;
      const options = {
        responseFormat: 'json',
        responseSchema: schema,
        maxTokens: 8192
      };

      // Use multimodal for images and PDFs with Gemini, text processing otherwise
      const isGeminiPdf = AI_ENVIRONMENT.AI_PROVIDER === 'gemini' && file.type === 'application/pdf';

      if (file.type.startsWith('image/') || isGeminiPdf) {
        response = await processMultimodalDocument(file, prompt, options);
      } else {
        const extractedText = await extractTextBasedOnType(file);
        response = await processTextDocument(extractedText, prompt, options);
      }

      const result = parseAIResponse(response, {});

      return {
        success: true,
        entityType,
        data: result,
        extractedAt: new Date().toISOString(),
        aiModel: AI_ENVIRONMENT.AI_PROVIDER === 'gemini' ? AI_CONFIG.GEMINI_MODEL : AI_CONFIG.OPENAI_VISION_MODEL,
        tripContextUsed: tripContext !== null
      };
    } catch (error) {
      console.error(`Entity extraction failed for ${entityType}:`, error);
      return {
        success: false,
        entityType,
        error: error.message,
        data: {},
        extractedAt: new Date().toISOString()
      };
    }
  },

  /**
   * Gets current AI service configuration
   * @returns {Object} Configuration information
   */
  getConfig() {
    return {
      ...AI_CONFIG,
      environment: AI_ENVIRONMENT,
      // Note: API key is managed securely by Cloud Functions
      cloudFunctionsEnabled: true,
      authenticationRequired: true,
      supportedFileTypes: [
        // Images (AI Vision support)
        'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp', 'image/bmp', 'image/tiff',
        // PDF (text extraction + AI analysis)
        'application/pdf',
        // Text-based documents (direct text processing)
        'text/plain', 'text/csv', 'text/html', 'application/json', 'application/xml', 'text/xml',
        // Office documents (requires text extraction)
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-powerpoint',
        'text/rtf',
        'application/rtf',
        'application/vnd.oasis.opendocument.text',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.presentation'
      ],
      errorStatistics: aiErrorHandler.getErrorStatistics()
    };
  }
};
